import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis
} from "recharts";
import { useAuth } from "../context/AuthContext";
import { useAppContext } from "../context/AppContext";
import type { RuntimeConfig } from "../pages/ConfigPage";

type SystemMetric = {
  label: string;
  value: string;
  detail?: string;
};

type SystemMonitorResponse = {
  metrics?: SystemMetric[];
  updatedAt?: string;
  snapshot?: SystemMonitorSnapshot;
  history?: SystemMonitorSnapshot[];
  hostInfo?: SystemMonitorHostInfo;
  error?: string;
};

type SystemMonitorSnapshot = {
  timestamp?: string;
  cpuPercent?: number;
  appCpuPercent?: number;
  gpuPercent?: number;
  appGpuPercent?: number;
  appGpuAvailable?: boolean;
  memoryPercent?: number;
  appMemPercent?: number;
  cpuCount?: number;
  load1?: number;
  load5?: number;
  load15?: number;
  netInBytesSec?: number;
  netOutBytesSec?: number;
};

type SystemMonitorPoint = {
  time: string;
  timestampMs: number;
  cpuPercent: number;
  appCpuPercent: number;
  gpuPercent: number;
  appGpuPercent: number | null;
  load1: number;
  netInMbitSec: number;
  netOutMbitSec: number;
};

type SystemMonitorHostInfo = {
  hostname?: string;
  uptimeSeconds?: number;
  os?: string;
  platform?: string;
  platformFamily?: string;
  diskFreeBytes?: number;
  diskTotalBytes?: number;
  analyticsDbSizeBytes?: number;
};

type ConfigResponse = {
  config?: RuntimeConfig;
  error?: string;
};

type SystemMonitorRangeOption = "latest" | "5m" | "15m" | "1h" | "3h" | "6h" | "12h" | "24h" | "all";

const SYSTEM_MONITOR_RANGE_OPTIONS: { value: SystemMonitorRangeOption; label: string }[] = [
  { value: "latest", label: "Latest" },
  { value: "5m", label: "Last 5 min" },
  { value: "15m", label: "Last 15 min" },
  { value: "1h", label: "Last hour" },
  { value: "3h", label: "Last 3 hours" },
  { value: "6h", label: "Last 6 hours" },
  { value: "12h", label: "Last 12 hours" },
  { value: "24h", label: "Last 24 hours" },
  { value: "all", label: "All" }
];

const SYSTEM_MONITOR_RANGE_TO_DURATION_MS: Record<SystemMonitorRangeOption, number | null> = {
  latest: null,
  "5m": 5 * 60 * 1000,
  "15m": 15 * 60 * 1000,
  "1h": 60 * 60 * 1000,
  "3h": 3 * 60 * 60 * 1000,
  "6h": 6 * 60 * 60 * 1000,
  "12h": 12 * 60 * 60 * 1000,
  "24h": 24 * 60 * 60 * 1000,
  all: null
};

const SystemMonitorSystem = () => {
  const { authorizedFetch, resolveApiPath } = useAuth();
  const { serverConfig } = useAppContext();
  const analyticsEnabled = serverConfig.analyticsEnabled;
  const theme = useTheme();
  const [metrics, setMetrics] = useState<SystemMetric[] | null>(null);
  const [hostInfo, setHostInfo] = useState<SystemMonitorHostInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [configLoading, setConfigLoading] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);
  const [enableSystemMonitor, setEnableSystemMonitor] = useState<boolean | null>(null);
  const [analyticsConfig, setAnalyticsConfig] = useState<RuntimeConfig["analytics"] | null>(null);
  const [history, setHistory] = useState<SystemMonitorPoint[]>([]);
  const [monitorRange, setMonitorRange] = useState<SystemMonitorRangeOption>("latest");

  const roundToFiveDecimals = useCallback((value: number) => Math.round(value * 1e5) / 1e5, []);

  const chartColors = useMemo(
    () => ({
      cpu: theme.palette.primary.main,
      appCpu: theme.palette.secondary.main,
      gpu: theme.palette.info.main,
      appGpu: theme.palette.mode === "dark" ? "#c5e1a5" : "#558b2f",
      load: theme.palette.warning.main,
      netIn: theme.palette.success.main,
      netOut: theme.palette.error.main,
      grid: theme.palette.divider
    }),
    [theme.palette.mode]
  );

  const normalizedMetrics = useMemo(
    () =>
      (metrics ?? []).filter(
        (metric) =>
          typeof metric?.label === "string" &&
          metric.label.trim().length > 0 &&
          typeof metric?.value === "string" &&
          metric.value.trim().length > 0
      ),
    [metrics]
  );

  const metricMap = useMemo(() => {
    const map = new Map<string, SystemMetric>();
    normalizedMetrics.forEach((metric) => {
      map.set(metric.label.toLowerCase(), metric);
    });
    return map;
  }, [normalizedMetrics]);

  const cpuMetric = metricMap.get("cpu usage");
  const appCpuMetric = metricMap.get("skyview cpu usage");
  const gpuMetric = metricMap.get("gpu usage");
  const appGpuMetric = metricMap.get("skyview gpu usage");
  const appMemoryMetric = metricMap.get("skyview memory usage");
  const loadMetric = metricMap.get("server load (1/5/15)");
  const netInMetric = metricMap.get("network in");
  const netOutMetric = metricMap.get("network out");
  const memoryMetric = metricMap.get("memory usage");
  const highlightColor = theme.palette.mode === "dark" ? theme.palette.primary.main : theme.palette.primary.dark;
  const appCpuDisplay = useMemo(() => {
    if (history.length > 0) {
      const latest = history.reduce((current, point) =>
        point.timestampMs > current.timestampMs ? point : current
      );
      if (Number.isFinite(latest.appCpuPercent)) {
        return `${latest.appCpuPercent.toFixed(1)}%`;
      }
    }
    return appCpuMetric?.value ?? null;
  }, [appCpuMetric?.value, history]);

  const fetchConfig = useCallback(
    async (isActive: () => boolean) => {
      setConfigLoading(true);
      setConfigError(null);
      try {
        const response = await authorizedFetch(resolveApiPath("/api/config"));
        const payload = (await response.json().catch(() => null)) as ConfigResponse | null;

        if (!response.ok) {
          throw new Error(payload && typeof payload.error === "string" ? payload.error : "Unable to load configuration");
        }

        if (!isActive()) {
          return;
        }

        const nextConfig = payload?.config ?? null;
        setEnableSystemMonitor(typeof nextConfig?.enableSystemMonitor === "boolean" ? nextConfig.enableSystemMonitor : null);
        setAnalyticsConfig(nextConfig?.analytics ?? null);
      } catch (err) {
        if (!isActive()) {
          return;
        }
        setEnableSystemMonitor(null);
        setConfigError(err instanceof Error ? err.message : "Unable to load configuration");
      } finally {
        if (isActive()) {
          setConfigLoading(false);
        }
      }
    },
    [authorizedFetch, resolveApiPath]
  );

  const fetchAnalyticsDbSize = useCallback(
    async (config: RuntimeConfig["analytics"]): Promise<number | null> => {
      if (!config?.enabled) {
        return null;
      }
      const response = await authorizedFetch("/api/analytics/connection/test", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(config)
      });
      if (!response.ok) {
        return null;
      }
      const payload = (await response.json().catch(() => null)) as { dbSizeBytes?: number; state?: string } | null;
      if (payload?.state !== "connected") {
        return null;
      }
      return typeof payload?.dbSizeBytes === "number" ? payload.dbSizeBytes : null;
    },
    [authorizedFetch]
  );

  const buildPoint = useCallback(
    (snapshot: SystemMonitorSnapshot, fallbackTimestamp?: string) => {
      const timestamp = typeof snapshot.timestamp === "string" ? snapshot.timestamp : fallbackTimestamp ?? "";
      const date = timestamp ? new Date(timestamp) : new Date();
      const timeLabel = new Intl.DateTimeFormat(undefined, {
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit"
      }).format(date);

      const cpuCount = typeof snapshot.cpuCount === "number" ? snapshot.cpuCount : 0;
      const appCpuRaw = typeof snapshot.appCpuPercent === "number" ? snapshot.appCpuPercent : 0;
      const normalizedAppCpu = cpuCount > 0 ? appCpuRaw / cpuCount : appCpuRaw;

      return {
        time: timeLabel,
        timestampMs: date.getTime(),
        cpuPercent: typeof snapshot.cpuPercent === "number" ? snapshot.cpuPercent : 0,
        appCpuPercent: normalizedAppCpu,
        gpuPercent: typeof snapshot.gpuPercent === "number" ? snapshot.gpuPercent : 0,
        appGpuPercent: snapshot.appGpuAvailable === false ? null : typeof snapshot.appGpuPercent === "number" ? snapshot.appGpuPercent : 0,
        load1: typeof snapshot.load1 === "number" ? snapshot.load1 : 0,
        netInMbitSec:
          typeof snapshot.netInBytesSec === "number"
            ? roundToFiveDecimals((snapshot.netInBytesSec * 8) / 1_000_000)
            : 0,
        netOutMbitSec:
          typeof snapshot.netOutBytesSec === "number"
            ? roundToFiveDecimals((snapshot.netOutBytesSec * 8) / 1_000_000)
            : 0
      };
    },
    [roundToFiveDecimals]
  );

  const fetchSystemMonitor = useCallback(
    async (isActive: () => boolean, options?: { background?: boolean }) => {
      const background = Boolean(options?.background);
      if (!background) {
        setLoading(true);
      }
      setError(null);
      try {
        const params = new URLSearchParams();
        if (analyticsEnabled && monitorRange !== "latest") {
          params.set("range", monitorRange);
        }
        const path = `/api/server/system-monitor${params.toString() ? `?${params.toString()}` : ""}`;
        const response = await authorizedFetch(resolveApiPath(path));
        const payload = (await response.json().catch(() => null)) as SystemMonitorResponse | null;

        if (!response.ok) {
          throw new Error(
            payload && typeof payload.error === "string" ? payload.error : "Unable to load system monitor data"
          );
        }

        if (!isActive()) {
          return;
        }

        setMetrics(payload?.metrics ?? []);
        setHostInfo((previous) => {
          if (!payload?.hostInfo) {
            return previous;
          }
          return { ...(previous ?? {}), ...payload.hostInfo };
        });
        if (analyticsEnabled && analyticsConfig && payload?.hostInfo?.analyticsDbSizeBytes == null) {
          const sizeBytes = await fetchAnalyticsDbSize(analyticsConfig);
          if (isActive() && sizeBytes != null) {
            setHostInfo((previous) => ({ ...(previous ?? {}), analyticsDbSizeBytes: sizeBytes }));
          }
        }
        if (isActive()) {
          if (Array.isArray(payload?.history) && payload.history.length > 0) {
            setHistory(
              payload.history
                .map((sample) => buildPoint(sample, payload?.updatedAt ?? undefined))
                .filter((point) => Boolean(point.time))
            );
          } else if (payload?.snapshot) {
            setHistory([buildPoint(payload.snapshot, payload?.updatedAt ?? undefined)]);
          }
        }
      } catch (err) {
        if (!isActive()) {
          return;
        }
        setMetrics(null);
        setError(err instanceof Error ? err.message : "Unable to load system monitor data");
      } finally {
        if (isActive() && !background) {
          setLoading(false);
        }
      }
    },
    [analyticsConfig, analyticsEnabled, authorizedFetch, buildPoint, fetchAnalyticsDbSize, monitorRange, resolveApiPath]
  );

  useEffect(() => {
    let active = true;

    void fetchConfig(() => active);

    return () => {
      active = false;
    };
  }, [fetchConfig]);

  useEffect(() => {
    if (!analyticsEnabled) {
      setMonitorRange("latest");
    }
  }, [analyticsEnabled]);

  useEffect(() => {
    if (enableSystemMonitor !== true) {
      setMetrics(null);
      setHostInfo(null);
      setError(null);
      setHistory([]);
      return;
    }

    let active = true;

    void fetchSystemMonitor(() => active);
    const intervalId = window.setInterval(() => {
      void fetchSystemMonitor(() => active, { background: true });
    }, 5000);

    return () => {
      active = false;
      window.clearInterval(intervalId);
    };
  }, [enableSystemMonitor, fetchSystemMonitor, monitorRange]);

  const filteredHistory = useMemo(() => {
    if (analyticsEnabled && monitorRange !== "latest") {
      const duration = SYSTEM_MONITOR_RANGE_TO_DURATION_MS[monitorRange];
      if (!duration || history.length === 0) {
        return history;
      }
      const nowMs = Date.now();
      const latest = history.reduce((max, point) => Math.max(max, point.timestampMs), 0);
      const windowEnd = Math.max(nowMs, latest);
      const windowStart = windowEnd - duration;
      const bucketMs = duration <= 6 * 60 * 60 * 1000 ? 5_000 : 60_000;
      const formatter = new Intl.DateTimeFormat(undefined, {
        hour12: false,
        ...(duration >= 12 * 60 * 60 * 1000 ? { month: "short", day: "numeric" } : {}),
        hour: "2-digit",
        minute: "2-digit",
        ...(bucketMs < 60_000 ? { second: "2-digit" } : {})
      });

      const pointsByBucket = new Map<number, SystemMonitorPoint>();
      history.forEach((point) => {
        const key = Math.floor(point.timestampMs / bucketMs) * bucketMs;
        if (!pointsByBucket.has(key)) {
          pointsByBucket.set(key, { ...point, timestampMs: key, time: formatter.format(new Date(key)) });
        }
      });

      const filled: SystemMonitorPoint[] = [];
      for (let ts = windowStart; ts <= windowEnd; ts += bucketMs) {
        const existing = pointsByBucket.get(Math.floor(ts / bucketMs) * bucketMs);
        if (existing) {
          filled.push(existing);
        } else {
          filled.push({
            time: formatter.format(new Date(ts)),
            timestampMs: ts,
            cpuPercent: 0,
            appCpuPercent: 0,
            gpuPercent: 0,
            appGpuPercent: 0,
            load1: 0,
            netInMbitSec: 0,
            netOutMbitSec: 0
          });
        }
      }
      return filled;
    }
    if (!analyticsEnabled || monitorRange === "latest") {
      return history;
    }
    const duration = SYSTEM_MONITOR_RANGE_TO_DURATION_MS[monitorRange];
    if (!duration || history.length === 0) {
      return history;
    }
    const latestTimestamp = Math.max(...history.map((point) => point.timestampMs));
    const cutoff = latestTimestamp - duration;
    return history.filter((point) => point.timestampMs >= cutoff);
  }, [analyticsEnabled, history, monitorRange]);

  const hasCharts = filteredHistory.length > 1;
  const hasAppCpu = filteredHistory.some((point) => point.appCpuPercent > 0);
  const hasAppGpu = filteredHistory.some((point) => (point.appGpuPercent ?? 0) > 0);

  const formattedUptime = useCallback((uptimeSeconds?: number) => {
    if (typeof uptimeSeconds !== "number" || !Number.isFinite(uptimeSeconds) || uptimeSeconds <= 0) {
      return null;
    }
    const totalSeconds = Math.floor(uptimeSeconds);
    const days = Math.floor(totalSeconds / 86_400);
    const hours = Math.floor((totalSeconds % 86_400) / 3_600);
    const minutes = Math.floor((totalSeconds % 3_600) / 60);
    const seconds = totalSeconds % 60;
    const parts: string[] = [];
    if (days > 0) {
      parts.push(`${days}d`);
    }
    if (hours > 0 || days > 0) {
      parts.push(`${hours}h`);
    }
    if (minutes > 0 || days === 0) {
      parts.push(`${minutes}m`);
    }
    if (parts.length === 0 && seconds > 0) {
      parts.push(`${seconds}s`);
    }
    return parts.join(" ");
  }, []);

  const formatBytes = useCallback((value?: number) => {
    if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
      return null;
    }
    const units = ["B", "KB", "MB", "GB", "TB", "PB"];
    let remaining = value;
    let index = 0;
    while (remaining >= 1024 && index < units.length - 1) {
      remaining /= 1024;
      index += 1;
    }
    if (remaining >= 100) {
      return `${remaining.toFixed(0)} ${units[index]}`;
    }
    if (remaining >= 10) {
      return `${remaining.toFixed(1)} ${units[index]}`;
    }
    return `${remaining.toFixed(2)} ${units[index]}`;
  }, []);

  const hostSummaryItems = useMemo(() => {
    if (!hostInfo) {
      return null;
    }
    const uptimeLabel = formattedUptime(hostInfo.uptimeSeconds);
    const diskFreeLabel = formatBytes(hostInfo.diskFreeBytes);
    const diskTotalLabel = formatBytes(hostInfo.diskTotalBytes);
    const dbSizeLabel = formatBytes(hostInfo.analyticsDbSizeBytes);
    const items = [
      hostInfo.hostname ? { label: "Host", value: hostInfo.hostname } : null,
      uptimeLabel ? { label: "Uptime", value: uptimeLabel } : null,
      hostInfo.os ? { label: "OS", value: hostInfo.os } : null,
      hostInfo.platform ? { label: "Platform", value: hostInfo.platform } : null,
      diskFreeLabel && diskTotalLabel
        ? { label: "Disk", value: `${diskFreeLabel} free / ${diskTotalLabel} available` }
        : null,
      dbSizeLabel ? { label: "DB", value: dbSizeLabel } : null
    ].filter((entry): entry is { label: string; value: string } => Boolean(entry));
    if (items.length === 0) {
      return null;
    }
    return items;
  }, [formatBytes, formattedUptime, hostInfo]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, p: 0.2 }}>
      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", ml: "auto" }}>
          {analyticsEnabled && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" sx={{ whiteSpace: "nowrap", color: "text.secondary" }}>
                Range
              </Typography>
              <FormControl variant="standard" sx={{ minWidth: 140 }}>
                <Select
                  value={monitorRange}
                  onChange={(event) => setMonitorRange(event.target.value as SystemMonitorRangeOption)}
                  sx={{
                    minHeight: 32,
                    color: "text.secondary",
                    "& .MuiSelect-select": { py: 0.5 },
                    "& .MuiSvgIcon-root": { color: "text.secondary" }
                  }}
                >
                  {SYSTEM_MONITOR_RANGE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </Box>
      </Box>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={2}>
          {configLoading && (
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">
                Loading configuration...
              </Typography>
            </Box>
          )}
          {configError && <Alert severity="error">{configError}</Alert>}
        </Stack>
        <Box mt={1}>
          {enableSystemMonitor === false ? (
            <Alert severity="info">System monitor is disabled.</Alert>
          ) : loading ? (
            <Box display="flex" alignItems="center" justifyContent="center" py={3}>
              <CircularProgress size={32} />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : (
            <Stack spacing={1.5}>
              {normalizedMetrics.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No system monitor data reported.
                </Typography>
              ) : (
                <Stack spacing={1.5}>
                  {(cpuMetric || appCpuMetric || gpuMetric || appGpuMetric || loadMetric || memoryMetric || appMemoryMetric) && (
                    <Box>
                      <Box sx={{ display: "flex", gap: 3, alignItems: "baseline" }}>
                        {cpuMetric && (
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
                            CPU usage <span style={{ color: highlightColor }}>{cpuMetric.value}</span>
                          </Typography>
                        )}
                        {appCpuMetric && appCpuDisplay && (
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
                            SkyView CPU <span style={{ color: highlightColor }}>{appCpuDisplay}</span>
                          </Typography>
                        )}
                        {gpuMetric && (
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
                            GPU usage <span style={{ color: highlightColor }}>{gpuMetric.value}</span>
                          </Typography>
                        )}
                        {appGpuMetric && (
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
                            SkyView GPU <span style={{ color: highlightColor }}>{appGpuMetric.value}</span>
                          </Typography>
                        )}
                        {loadMetric && (
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
                            Load <span style={{ color: highlightColor }}>{loadMetric.value}</span>
                          </Typography>
                        )}
                        {memoryMetric && (
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, fontSize: "0.85rem", marginLeft: "auto" }}
                          >
                            Memory <span style={{ color: highlightColor }}>{memoryMetric.value}</span>
                          </Typography>
                        )}
                        {appMemoryMetric && (
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
                            SkyView memory <span style={{ color: highlightColor }}>{appMemoryMetric.value}</span>
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ display: "flex", gap: 3, mt: 0.5 }}>
                        {cpuMetric && cpuMetric.detail && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.72rem" }}>
                            {cpuMetric.detail}
                          </Typography>
                        )}
                        {memoryMetric && memoryMetric.detail && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: "0.72rem", marginLeft: "auto", textAlign: "right" }}
                          >
                            {memoryMetric.detail}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                  {(netInMetric || netOutMetric) && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 1.25,
                        mt: -0.35
                      }}
                    >
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.2 }}>
                        <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}>
                          <Tooltip title="Incoming network throughput.">
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
                              Network in
                            </Typography>
                          </Tooltip>
                          <Tooltip title="Incoming traffic rate.">
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                fontSize: "0.85rem",
                                color: highlightColor
                              }}
                            >
                              {netInMetric?.value ?? "--"}
                            </Typography>
                          </Tooltip>
                        </Box>
                        {netInMetric?.detail && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.72rem" }}>
                            {netInMetric.detail}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ textAlign: "right" }}>
                        <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "flex-end", gap: 0.75 }}>
                          <Tooltip title="Outgoing network throughput.">
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
                              Network out
                            </Typography>
                          </Tooltip>
                          <Tooltip title="Outgoing traffic rate.">
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                fontSize: "0.85rem",
                                color: highlightColor
                              }}
                            >
                              {netOutMetric?.value ?? "--"}
                            </Typography>
                          </Tooltip>
                        </Box>
                        {netOutMetric?.detail && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.72rem" }}>
                            {netOutMetric.detail}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                </Stack>
              )}
            </Stack>
          )}
        </Box>
        {hostSummaryItems && (
          <Box mt={0.5} mb={1.5}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
              {hostSummaryItems.map((item, index) => (
                <span key={item.label}>
                  {index > 0 ? " • " : ""}
                  {item.label}: <span style={{ color: highlightColor, fontWeight: 600 }}>{item.value}</span>
                </span>
              ))}
            </Typography>
          </Box>
        )}
        {enableSystemMonitor !== false && (
          <Stack spacing={1.25} mt={2}>
            <Box>
              <Tooltip title="CPU utilization over the last minute.">
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  CPU usage
                </Typography>
              </Tooltip>
              <Box sx={{ width: "100%", height: 120 }}>
                {hasCharts ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={filteredHistory} margin={{ top: 6, right: 10, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="cpuGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={chartColors.cpu} stopOpacity={0.45} />
                          <stop offset="95%" stopColor={chartColors.cpu} stopOpacity={0.05} />
                        </linearGradient>
                        <linearGradient id="appCpuGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={chartColors.appCpu} stopOpacity={0.35} />
                          <stop offset="95%" stopColor={chartColors.appCpu} stopOpacity={0.05} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke={chartColors.grid} strokeDasharray="3 3" />
                      <XAxis dataKey="time" tick={{ fontSize: 11 }} interval="preserveEnd" />
                      <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                      <RechartsTooltip formatter={(value) => `${Number(value).toFixed(2)}%`} />
                      <Area
                        type="monotone"
                        dataKey="cpuPercent"
                        stroke={chartColors.cpu}
                        fill="url(#cpuGradient)"
                        strokeWidth={2}
                        name="CPU usage"
                      />
                      {hasAppCpu && (
                        <Area
                          type="monotone"
                          dataKey="appCpuPercent"
                          stroke={chartColors.appCpu}
                          fill="url(#appCpuGradient)"
                          strokeWidth={2}
                          name="SkyView CPU"
                        />
                      )}
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Gathering samples...
                  </Typography>
                )}
              </Box>
            </Box>
            <Box>
              <Tooltip title="GPU engine utilization over the selected range.">
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  GPU usage
                </Typography>
              </Tooltip>
              <Box sx={{ width: "100%", height: 120 }}>
                {hasCharts ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={filteredHistory} margin={{ top: 6, right: 10, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="gpuGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={chartColors.gpu} stopOpacity={0.45} />
                          <stop offset="95%" stopColor={chartColors.gpu} stopOpacity={0.05} />
                        </linearGradient>
                        <linearGradient id="appGpuGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={chartColors.appGpu} stopOpacity={0.35} />
                          <stop offset="95%" stopColor={chartColors.appGpu} stopOpacity={0.05} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke={chartColors.grid} strokeDasharray="3 3" />
                      <XAxis dataKey="time" tick={{ fontSize: 11 }} interval="preserveEnd" />
                      <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                      <RechartsTooltip formatter={(value) => `${Number(value).toFixed(2)}%`} />
                      <Area
                        type="monotone"
                        dataKey="gpuPercent"
                        stroke={chartColors.gpu}
                        fill="url(#gpuGradient)"
                        strokeWidth={2}
                        name="GPU usage"
                      />
                      {hasAppGpu && (
                        <Area
                          type="monotone"
                          dataKey="appGpuPercent"
                          stroke={chartColors.appGpu}
                          fill="url(#appGpuGradient)"
                          strokeWidth={2}
                          name="SkyView GPU"
                        />
                      )}
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Gathering samples...
                  </Typography>
                )}
              </Box>
            </Box>
            <Box>
              <Tooltip title="System load (1-minute average) over the last minute.">
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Server load
                </Typography>
              </Tooltip>
              <Box sx={{ width: "100%", height: 120 }}>
                {hasCharts ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={filteredHistory} margin={{ top: 6, right: 10, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="loadGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={chartColors.load} stopOpacity={0.45} />
                          <stop offset="95%" stopColor={chartColors.load} stopOpacity={0.05} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke={chartColors.grid} strokeDasharray="3 3" />
                      <XAxis dataKey="time" tick={{ fontSize: 11 }} interval="preserveEnd" />
                      <YAxis tick={{ fontSize: 11 }} />
                      <RechartsTooltip />
                      <Area
                        type="monotone"
                        dataKey="load1"
                        stroke={chartColors.load}
                        fill="url(#loadGradient)"
                        strokeWidth={2}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Gathering samples...
                  </Typography>
                )}
              </Box>
            </Box>
            <Box>
              <Tooltip title="Incoming and outgoing network throughput over the last minute.">
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Network traffic
                </Typography>
              </Tooltip>
              <Box sx={{ width: "100%", height: 130 }}>
                {hasCharts ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={filteredHistory} margin={{ top: 6, right: 10, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="netInGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={chartColors.netIn} stopOpacity={0.4} />
                          <stop offset="95%" stopColor={chartColors.netIn} stopOpacity={0.05} />
                        </linearGradient>
                        <linearGradient id="netOutGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={chartColors.netOut} stopOpacity={0.35} />
                          <stop offset="95%" stopColor={chartColors.netOut} stopOpacity={0.05} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke={chartColors.grid} strokeDasharray="3 3" />
                      <XAxis dataKey="time" tick={{ fontSize: 11 }} interval="preserveEnd" />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(value) => `${Number(value).toFixed(2)}`} />
                      <RechartsTooltip formatter={(value) => Number(value).toFixed(2)} />
                      <Area
                        type="monotone"
                        dataKey="netInMbitSec"
                        stroke={chartColors.netIn}
                        fill="url(#netInGradient)"
                        strokeWidth={2}
                        name="Incoming (Mbit/s)"
                      />
                      <Area
                        type="monotone"
                        dataKey="netOutMbitSec"
                        stroke={chartColors.netOut}
                        fill="url(#netOutGradient)"
                        strokeWidth={2}
                        name="Outgoing (Mbit/s)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Gathering samples...
                  </Typography>
                )}
              </Box>
            </Box>
          </Stack>
        )}
      </Paper>
    </Box>
  );
};

export default SystemMonitorSystem;
